/**
 * Per-event-type Smart Assignment configuration.
 *
 * Assignment rules used to be a single tenant-wide blob, which meant one
 * rotation and one set of rules across every kind of appointment. A teammate
 * taking a Touchup would advance their place in the On-Site Estimate rotation
 * and get skipped for the estimate they were actually next up for.
 *
 * Rules are now always scoped to one event type (the `EventTypes` taxonomy in
 * Settings). There is no company-wide rule set — every event type is
 * configured on its own and runs its own rotation, so volume in one never
 * costs anyone a turn in another.
 *
 * Persisted as JSON in `CompanySettings.smartAssignmentConfigJson`.
 */

import type { InstantBookingPreferences } from '../../types/instantBookingPreferences';
import { DEFAULT_INSTANT_BOOKING_PREFERENCES } from '../../types/instantBookingPreferences';

/** An event type code from the `EventTypes` taxonomy. */
export type SmartAssignmentScope = string;

export const SMART_ASSIGNMENT_CONFIG_VERSION = 3;

export interface SmartAssignmentConfig {
  version: number;
  /** Rules keyed by event type code. Every known event type has an entry. */
  eventTypes: Record<string, InstantBookingPreferences>;
}

export const EMPTY_SMART_ASSIGNMENT_CONFIG: SmartAssignmentConfig = {
  version: SMART_ASSIGNMENT_CONFIG_VERSION,
  eventTypes: {},
};

/** Minimal shape of an event type from MasterData / GlobalCodes. */
export interface EventTypeRef {
  code?: string | null;
  text?: string | null;
}

const withDefaults = (raw: unknown): InstantBookingPreferences => ({
  ...DEFAULT_INSTANT_BOOKING_PREFERENCES,
  ...(raw && typeof raw === 'object' ? (raw as Partial<InstantBookingPreferences>) : {}),
});

const readObject = (json: string | null | undefined): Record<string, unknown> | null => {
  if (!json) return null;
  try {
    const raw: unknown = JSON.parse(json);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    return raw as Record<string, unknown>;
  } catch {
    return null;
  }
};

const readRuleSetMap = (raw: unknown): Record<string, unknown> =>
  raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};

/**
 * Read the stored config and materialize one rule set per known event type.
 *
 * Handles both older shapes so nobody's behavior changes on deploy:
 * - v1 stored a bare tenant-wide `InstantBookingPreferences`
 * - v2 stored a company default plus per-event-type overrides
 *
 * In both cases the previous tenant-wide rules seed every event type that
 * doesn't already have its own, which reproduces what those tenants were
 * getting before rules were split apart.
 *
 * Event types are also the source of truth for which rule sets exist: codes
 * that no longer appear in `eventTypes` are dropped, so deleting an event type
 * in Settings doesn't leave rules nobody can reach. When the event type list
 * is empty (it failed to load) nothing is pruned, so a save can't wipe config.
 */
export function parseSmartAssignmentConfig(
  json: string | null | undefined,
  eventTypes: EventTypeRef[]
): SmartAssignmentConfig {
  const obj = readObject(json);

  let seed: InstantBookingPreferences = { ...DEFAULT_INSTANT_BOOKING_PREFERENCES };
  let stored: Record<string, unknown> = {};

  if (obj) {
    const version = typeof obj.version === 'number' ? obj.version : 1;
    if (version >= 3) {
      stored = readRuleSetMap(obj.eventTypes);
    } else if (version === 2) {
      seed = withDefaults(obj.companyDefault);
      stored = readRuleSetMap(obj.eventTypeOverrides);
    } else {
      seed = withDefaults(obj);
    }
  }

  const codes = eventTypes
    .map((type) => type.code)
    .filter((code): code is string => !!code);

  // Without a known event type list we can't tell a deleted code from an
  // unloaded one — keep everything rather than risk dropping live config.
  if (codes.length === 0) {
    const kept: Record<string, InstantBookingPreferences> = {};
    for (const [code, prefs] of Object.entries(stored)) {
      kept[code] = withDefaults(prefs);
    }
    return { version: SMART_ASSIGNMENT_CONFIG_VERSION, eventTypes: kept };
  }

  const result: Record<string, InstantBookingPreferences> = {};
  for (const code of codes) {
    result[code] = code in stored ? withDefaults(stored[code]) : { ...seed };
  }
  return { version: SMART_ASSIGNMENT_CONFIG_VERSION, eventTypes: result };
}

/**
 * The rules one booking form actually runs under, read straight from the
 * stored JSON for the form's event type code. Mirrors the backend reader
 * (`SmartAssignmentConfigReader.ResolveRuleSet`) so read-only surfaces such as
 * the booking modal and the form builder describe exactly what the engine
 * will do:
 * - v1 stored one tenant-wide rule set, which applies to every event type
 * - v2 uses the event type's override, else the company default
 * - v3 uses the event type's own entry, else the defaults (never another
 *   event type's rules)
 *
 * Unlike `parseSmartAssignmentConfig` this does not need the event type list,
 * so callers that only know a single code can use it without a taxonomy fetch.
 */
export function resolveEventTypePreferences(
  json: string | null | undefined,
  eventTypeCode: string | null | undefined
): InstantBookingPreferences {
  const obj = readObject(json);
  if (!obj) return { ...DEFAULT_INSTANT_BOOKING_PREFERENCES };

  const code = eventTypeCode ?? '';
  const version = typeof obj.version === 'number' ? obj.version : 1;

  if (version >= 3) {
    const stored = readRuleSetMap(obj.eventTypes);
    return code && code in stored
      ? withDefaults(stored[code])
      : { ...DEFAULT_INSTANT_BOOKING_PREFERENCES };
  }

  if (version === 2) {
    const overrides = readRuleSetMap(obj.eventTypeOverrides);
    return code && code in overrides
      ? withDefaults(overrides[code])
      : withDefaults(obj.companyDefault);
  }

  return withDefaults(obj);
}

export function serializeSmartAssignmentConfig(config: SmartAssignmentConfig): string {
  return JSON.stringify({
    version: SMART_ASSIGNMENT_CONFIG_VERSION,
    eventTypes: config.eventTypes,
  });
}

/** The rules for one event type. */
export function resolveScopedPreferences(
  config: SmartAssignmentConfig,
  scope: SmartAssignmentScope
): InstantBookingPreferences {
  return config.eventTypes[scope] ?? DEFAULT_INSTANT_BOOKING_PREFERENCES;
}

/** Write edited rules back into one event type, leaving the others alone. */
export function writeScopedPreferences(
  config: SmartAssignmentConfig,
  scope: SmartAssignmentScope,
  preferences: InstantBookingPreferences
): SmartAssignmentConfig {
  if (!scope) return config;
  return {
    ...config,
    eventTypes: { ...config.eventTypes, [scope]: preferences },
  };
}

const normalizeLabel = (value: string) => value.toLowerCase().replace(/[^a-z]+/g, '');

/**
 * Which event type the panel opens on. On-Site Estimate is the one that
 * matters most in practice, so it wins when the tenant has it; otherwise the
 * first event type in the taxonomy's own order.
 */
export function pickInitialScope(eventTypes: EventTypeRef[]): SmartAssignmentScope {
  const usable = eventTypes.filter((type) => !!type.code);
  const onSiteEstimate = usable.find((type) =>
    normalizeLabel(`${type.text ?? ''} ${type.code ?? ''}`).includes('onsiteestimate')
  );
  return (onSiteEstimate ?? usable[0])?.code ?? '';
}
